/**
 * Generates HTML email template for "Passenger Details Complete".
 * Sent to the broker when the client finishes submitting passenger
 * details on the public trip-sheet page. Mirrors the client-facing
 * finalize-passenger-profiles email in layout so the broker sees the
 * same aircraft/leg context alongside the submitted passenger list.
 */

import { icon, spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter } from '../emailLayout';
import { resolveAircraftImageUrl, getEffectiveAircraft } from '@/features/tails/utils';
import { formatUTCToDateTime } from '@/utils/formatters';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { selectPrimaryBidOption } from '@/features/quotes/utils/selectAcceptedBidOption';
import { formatPrice, trunc2 } from '@/utils';

const buildAircraftCardHtml = async (option, quote) => {
  const aircraft = getEffectiveAircraft(option?.aircraft, quote);
  const operator = option?.operator;

  const imageUrl = await resolveAircraftImageUrl(aircraft);

  const modelName = (() => {
    if (!aircraft) return '';
    if (aircraft.display_name_override) return aircraft.display_name_override;
    if (aircraft.aircraft_make_model) {
      const { manufacturer } = aircraft.aircraft_make_model;
      const makeModel = aircraft.make_model || aircraft.aircraft_make_model.make_model || '';
      if (manufacturer && makeModel) {
        const modelPart = makeModel.replace(new RegExp(`^${manufacturer}[- ]*`, 'i'), '');
        return `${manufacturer} ${modelPart}`.trim();
      }
      return makeModel || '';
    }
    return aircraft.make_model || aircraft.tail_number || '';
  })();

  const price = formatPrice(trunc2(option?.total_price || option?.final_price));

  const badges = [];
  if (operator?.argus_rating && operator.argus_rating !== 'No') {
    badges.push({ label: operator.argus_rating.toUpperCase(), color: '#374151', bg: '#e8e9eb', iconName: 'trophy' });
  }
  if (operator?.wyvern_rating && operator.wyvern_rating !== 'No') {
    badges.push({ label: 'WINGMAN', color: '#374151', bg: '#e8e9eb', iconName: 'medal' });
  }
  if (aircraft?.aircraft_features?.no_aog) {
    badges.push({ label: 'NO AOG', color: '#15803D', bg: '#DCFCE7', iconName: 'check' });
  }

  const badgesHtml = badges.map(b =>
    `<span style="display:inline-block;padding:4px 10px;background:${b.bg};color:${b.color};font-size:11px;font-weight:600;border-radius:16px;margin-right:6px;margin-bottom:4px;">${icon(b.iconName, 14, b.color)} ${b.label}</span>`
  ).join('');

  return `
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          ${imageUrl ? `
          <tr>
            <td style="padding:0;line-height:0;font-size:0;">
              <img src="${imageUrl}" alt="${modelName}" width="600" style="display:block;width:100%;height:auto;object-fit:cover;max-height:280px;" />
            </td>
          </tr>` : ''}
          <tr>
            <td style="padding:20px 32px;background-color:#FFFFFF;">
              <h2 style="margin:0 0 4px;font-size:20px;font-weight:500;color:#171717;">${aircraft?.tail_number || ''}</h2>
              <p style="margin:0 0 2px;font-size:13px;color:#6B7280;">${icon('plane', 14, '#6B7280')} ${modelName}</p>

              <div style="display:inline-block;padding:6px 14px;background-color:#E8F4FD;border-radius:16px;margin-bottom:12px;">
                <span style="font-size:14px;font-weight:700;color:#0C3E71;">${icon('priceTag', 14, '#0C3E71')} ${price}</span>
              </div>

              ${badgesHtml ? `<div style="margin-top:8px;">${badgesHtml}</div>` : ''}
            </td>
          </tr>
        </table>`;
};

const formatDobDisplay = (dob) => {
  if (!dob || typeof dob !== 'string') return '';
  const iso = dob.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  return dob;
};

/**
 * Build the HTML email body for the passenger-details-complete broker notification.
 * @param {Object} params
 * @param {Object} params.quote - Quote data
 * @param {Array}  params.flights - Flight data array
 * @param {string} [params.clientName] - Display name of the submitting client
 * @param {string} [params.reviewUrl] - Optional broker-side deep link to review the trip sheet
 * @param {Object} [params.organization] - Broker organization
 * @returns {string} HTML email body
 */
export const buildPassengerDetailsCompleteEmailHtml = async ({
  quote,
  flights = [],
  clientName,
  reviewUrl,
  organization,
}) => {
  const allBidOptions = quote?.bid_items?.flatMap(bi => bi.bid?.bid_options || []) || [];
  const picked = selectPrimaryBidOption(allBidOptions);
  const optionsToRender = picked ? [picked] : [];

  const aircraftCardsHtml = (
    await Promise.all(optionsToRender.map((option) => buildAircraftCardHtml(option, quote)))
  ).join(`\n        ${spacer()}\n`);

  const displayClientName = clientName
    || [quote?.client?.first_name, quote?.client?.last_name].filter(Boolean).join(' ')
    || quote?.client?.email
    || 'The client';

  const legRows = (flights || []).map((f, i) => `
                <tr>
                  <td style="padding:12px 0px;border-bottom:1px solid #E5E7EB;${i === 0 ? 'border-top:1px solid #E5E7EB;' : ''}">
                    <span style="font-size:14px;font-weight:600;background-color: #e8e9eb; border-radius: 16px; padding:2px 10px;">${icon('flightTakeoff', 14, '#374151')} ${f.departure_airport || '—'}</span>
                    <span style="margin:0 8px;vertical-align:middle;">${icon('arrowRight', 14, '#9CA3AF')}</span>
                    <span style="font-size:14px;font-weight:600;background-color: #e8e9eb; border-radius: 16px; padding:2px 10px;">${icon('flightLand', 14, '#515c6e')} ${f.arrival_airport || '—'}</span>
                  </td>
                  <td style="padding:12px 32px;border-bottom:1px solid #E5E7EB;${i === 0 ? 'border-top:1px solid #E5E7EB;' : ''}text-align:right;">
                    <span style="font-size:13px;color:#6B7280;">${icon('calendar', 13, '#6B7280')} ${formatUTCToDateTime(f.departure_date, getAirportTimezone(f.departure_airport), "MMM D")}</span>
                    <span style="font-size:13px;color:#6B7280;margin-left:12px;">${icon('time', 13, '#6B7280')} ${formatUTCToDateTime(f.departure_date, getAirportTimezone(f.departure_airport), "HH:mm")}</span>
                  </td>
                </tr>`).join('');

  return `
${emailDocOpen({ title: 'Passenger Details Complete' })}
${emailHeader({ title: 'Passenger Details Complete', subtitle: 'The client has submitted requested passenger details.', organization, isBroker: true })}

        ${spacer()}

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:16px 32px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <span style="font-size:11px;text-transform:uppercase;color:#9CA3AF;letter-spacing:0.5px;font-weight:600;">Flight Itenerary For</span><br/>
                    <span style="font-size:16px;font-weight:600;color:#111827;">${clientName}</span>
                  </td>
                  <td align="right">
                    <span style="font-size:11px;text-transform:uppercase;color:#9CA3AF;letter-spacing:0.5px;font-weight:600;">Booking Number</span><br/>
                    <span style="font-size:16px;font-weight:600;color:#111827;">${quote?.quote_number}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>

         ${spacer()}

        <!-- ===== CONFIRMATION CARD ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td align="left" style="padding:28px 32px;">
              <div style="width:64px;height:64px;background-color:#FFF1EC;border-radius:50%;text-align:center;line-height:64px;margin-bottom:16px;">
                ${icon('editDocumentFill', 28, '#E8630A')}
              </div>
              <p style="margin:0 0 8px;font-size:17px;font-weight:400;color:#5C5C5C;">
                The passenger profiles have been added by the client and are ready for your review.
              </p>
              ${reviewUrl ? `
              <br>
              <a href="${reviewUrl}" style="display:inline-block;padding:12px 32px;background-color:#0C3E71;color:#FFFFFF;text-decoration:none;font-size:14px;font-weight:400;border-radius:24px;">View Passenger Details</a>` : ''}
            </td>
          </tr>
        </table>

        ${aircraftCardsHtml ? `
        ${spacer()}

        <!-- ===== AIRCRAFT CARDS ===== -->
        ${aircraftCardsHtml}` : ''}

        ${legRows ? `
        ${spacer()}

        <!-- ===== LEG DETAILS ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#F7F7F7;overflow:hidden;">
          <tr>
            <td style="padding:20px 0;">
              <span style="font-size:18px;text-transform:uppercase;color:#5C5C5C;letter-spacing:0.8px;font-weight:600;">Leg Details</span>
            </td>
          </tr>
          <tr>
            <td style="padding:0;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                ${legRows}
              </table>
            </td>
          </tr>
        </table>` : ''}

        ${spacer()}

${emailFooter()}
${emailDocClose()}`;
};
